import { useEffect, useState } from 'react';

import { TextField, CircularProgress } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import useDebouncedEffect from 'hooks/useDebouncedEffect';

import { FaSearch } from 'react-icons/fa';
import {
  Card,
  Col,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from 'reactstrap';
import api from 'services/api';
import { iMaterial } from 'views/pages/Materiais';

interface Props {
  descricao?: string;
  children?: any;
  handleSelecionarItem: (itemDomsge: iAutoCompleteItem | null) => void;
  hideTitle?: boolean;
  maxWidth?: string;
  noAutoFocus?: boolean;
  emprCodigo?: string | undefined;
  label?: string | undefined;
  geral?: boolean;
  valorInput?: number | null;
  ignorarMargem?: boolean;
  usarDiv?: boolean;
  compact?: boolean;
  obrigatorio?: boolean;
  marginBottom?: string;
}

export interface iAutoCompleteItem {
  id: number;
  codigo: string;
  descricao: string;
}

interface iRetornoMaterial {
  id: number;
  codigoSap: string | null;
  diteCodigo: string;
  descricao: string;
}

interface iRetornoMaterial {
  id: number;
  codigoSap: string | null;
  diteCodigo: string;
  descricao: string;
}

function converterMaterial(material: iRetornoMaterial): iAutoCompleteItem {
  const codigo = material.codigoSap ?? material.diteCodigo;

  return {
    id: material.id,
    codigo,
    descricao: `${codigo}${
      material.diteCodigo ? ` (SGE: ${material.diteCodigo})` : ''
    } - ${material.descricao}`,
  };
}

export default function AutocompleteItemNovo({
  descricao,
  children,
  handleSelecionarItem,
  hideTitle,
  maxWidth,
  noAutoFocus,
  valorInput,
  label,
  usarDiv = false,
  compact = false,
  ignorarMargem = false,
  obrigatorio = false,
  emprCodigo,
  geral,
  marginBottom = 'mb-4',
}: Props): JSX.Element {
  const [loadingItens, setLoadingItens] = useState(false);
  // const [loadingLista, setLoadingLista] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([] as iAutoCompleteItem[]);
  const [itemSelecionado, setItemSelecionado] =
    useState<iAutoCompleteItem | null>(null);

  useEffect(() => {
    if (!valorInput) {
      setItemSelecionado(null);
      setInputValue('');
      return;
    }

    const carregarMaterial = async () => {
      try {
        const { data } = await api.get(`/material/${valorInput}`);

        const item = converterMaterial(data);

        setItemSelecionado(item);
        setInputValue(item.descricao);
      } catch (error) {
        console.error(error);
      }
    };

    carregarMaterial();
  }, [valorInput]);

  useDebouncedEffect(
    () => {
      // Para que o usuário possa usar tanto % quanto * na pesquisa
      // Ex: cabo%aço ou cabo*aço
      const texto = inputValue.trim().replace(/\*/g, '%');

      if (texto.length < 2) {
        setOptions([]);
        return;
      }

      const carregar = async () => {
        try {
          setLoadingItens(true);

          const { data } = await api.get('/material/lista-filtrada', {
            params: {
              inputUsuario: texto,
              quantidadeRegistros: 30,
            },
          });

          setOptions(data.map(converterMaterial));
        } catch (error) {
          console.error(error);
          setOptions([]);
        } finally {
          setLoadingItens(false);
        }
      };

      carregar();
    },
    400,
    [inputValue],
  );

  const Wrapper = usarDiv ? 'div' : Card;

  return (
    <Wrapper className={compact ? 'mb-1' : 'shadow mt-3 mb-1'}>
      <Col className={`${ignorarMargem ? 'mt-0' : 'mt-4'} p-0`}>
        <FormGroup className={`${marginBottom}`}>
          {!hideTitle && (
            <h5 className="mb-3">
              {descricao ? `${descricao} | ` : ''} Vincular item
            </h5>
          )}

          <InputGroup
            className="input-group-alternative"
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'nowrap',
            }}
          >
            {!compact && (
              <InputGroupAddon
                addonType="prepend"
                style={{
                  flex: '0 0 auto',
                }}
              >
                <InputGroupText>
                  <FaSearch />
                </InputGroupText>
              </InputGroupAddon>
            )}

            <Autocomplete
              className={`${ignorarMargem ? 'mt-0' : 'mt-1'}`}
              style={{
                width: '100%',
                maxWidth: maxWidth || '51vw',
              }}
              fullWidth
              size="small"
              // open={open}
              options={options}
              // disabled={loadingLista}
              // loading={loadingItens || loadingLista}
              loading={loadingItens}
              // inputValue={inputValue}
              // onOpen={() => {
              //   setOpen(true);
              // }}
              // onClose={() => {
              //   setOpen(false);
              // }}
              getOptionSelected={(option: iAutoCompleteItem, value) =>
                option.id === value.id
              }
              getOptionLabel={option =>
                option?.codigo ? `${option.descricao}` : ''
              }
              loadingText="Carregando..."
              noOptionsText="Nada encontrado"
              value={itemSelecionado}
              // clearOnBlur
              clearOnEscape
              disableClearable={false}
              // onChange={(_, itemDomsge) => {
              //   setItemSelecionado(itemDomsge);
              //   handleSelecionarItem(itemDomsge);
              // }}
              onChange={(_, item, reason) => {
                setItemSelecionado(item);

                if (reason === 'clear') {
                  setInputValue('');
                  setOptions([]);
                } else {
                  setInputValue(item?.descricao ?? '');
                }

                handleSelecionarItem(item);
              }}
              filterOptions={lista => lista}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                  setInputValue(value);
                  return;
                }

                if (reason === 'clear') {
                  setInputValue('');
                  setItemSelecionado(null);
                  setOptions([]);
                }
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  inputRef={input => input && !noAutoFocus && input.focus()}
                  size="small"
                  required={obrigatorio}
                  label={label ?? 'Pesquise um item...'}
                  variant="outlined"
                  InputLabelProps={{ style: { fontSize: 13 } }}
                  InputProps={{
                    ...params.InputProps,
                    style: { fontSize: 13 },
                  }}
                />
              )}
            />

            {children}
          </InputGroup>
        </FormGroup>
      </Col>
    </Wrapper>
  );
}
